import React, { useContext, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { AppContext } from '../../../../context/AppContextProvider';
import { IPaginationParams } from '../../../../services/types';
import {
  DownloadQueueItemStatus,
  IDownloadQueueFilterParams,
} from '../../../../services/DownloadQueue/types';
import {
  useGetAllDownloadQueueQuery,
  useGetDownloadQueueCountQuery,
} from '../../../../services/DownloadQueue/hooks/useQuery';
import { useRetryDownloadQueueItemAsync } from '../../../../services/DownloadQueue/hooks/useMutation';
import { useGetAllDevicesByLicenseAsync } from '../../../../services/Devices/hooks/useMutation';

export type QuickFilter = 'hoje' | '7d' | 'todos' | 'personalizado';

export const QUICK_FILTER_LABELS: Record<QuickFilter, string> = {
  hoje: 'Hoje',
  '7d': 'Últimos 7 dias',
  todos: 'Todo o Período',
  personalizado: 'Personalizado',
};

export const useDownloadQueue = () => {
  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  const [pagination, setPagination] = useState<IPaginationParams>({ pageNumber: 1, pageSize: 25 });
  const [deviceId, setDeviceId] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<DownloadQueueItemStatus | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilter>('todos');
  const [filter, setFilter] = useState<IDownloadQueueFilterParams>({});

  const [devices, setDevices] = useState<{ id: number; name: string }[]>([]);
  const { mutateAsync: fetchDevices } = useGetAllDevicesByLicenseAsync();

  useEffect(() => {
    fetchDevices({}).then((data) => {
      setDevices((data || []).map((d: any) => ({ id: d.id, name: d.name })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: items = [],
    isLoading,
    isFetching,
    refetch,
  } = useGetAllDownloadQueueQuery(pagination, filter);

  const { data: totalCount = 0, refetch: refetchCount } = useGetDownloadQueueCountQuery(filter);

  // Contagens por status dentro do filtro atual, pros cards de resumo.
  const { data: queuedCount = 0, refetch: refetchQueuedCount } =
    useGetDownloadQueueCountQuery({ ...filter, status: 'Queued' });
  const { data: processingCount = 0, refetch: refetchProcessingCount } =
    useGetDownloadQueueCountQuery({ ...filter, status: 'Processing' });
  const { data: completedCount = 0, refetch: refetchCompletedCount } =
    useGetDownloadQueueCountQuery({ ...filter, status: 'Completed' });
  const { data: failedCount = 0, refetch: refetchFailedCount } =
    useGetDownloadQueueCountQuery({ ...filter, status: 'Failed' });

  const { mutateAsync: retry, isLoading: isRetrying } = useRetryDownloadQueueItemAsync();

  const applyFilters = (start: Dayjs | null = startDate, end: Dayjs | null = endDate) => {
    setFilter({
      deviceId,
      status,
      search: search || undefined,
      from: start ? start.startOf('day').toISOString() : undefined,
      to: end ? end.endOf('day').toISOString() : undefined,
    });
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleQuickDateFilter = (preset: QuickFilter) => {
    setActiveQuickFilter(preset);
    let newStart: Dayjs | null = null;
    let newEnd: Dayjs | null = null;

    if (preset === 'hoje') {
      newStart = dayjs().startOf('day');
      newEnd = dayjs().endOf('day');
    } else if (preset === '7d') {
      newStart = dayjs().subtract(7, 'day').startOf('day');
      newEnd = dayjs().endOf('day');
    }

    setStartDate(newStart);
    setEndDate(newEnd);
    setFilter((prev) => ({
      ...prev,
      from: newStart ? newStart.toISOString() : undefined,
      to: newEnd ? newEnd.toISOString() : undefined,
    }));
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleClearFilters = () => {
    setDeviceId(undefined);
    setStatus(undefined);
    setSearch('');
    setStartDate(null);
    setEndDate(null);
    setActiveQuickFilter('todos');
    setFilter({});
    setPagination({ pageNumber: 1, pageSize: pagination.pageSize });
  };

  const handleRefresh = () => {
    refetch();
    refetchCount();
    refetchQueuedCount();
    refetchProcessingCount();
    refetchCompletedCount();
    refetchFailedCount();
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({ pageNumber: 1, pageSize: parseInt(event.target.value, 10) });
  };

  const handleRetry = async (id: number) => {
    setIsShowLoadingComponent(true);
    try {
      await retry(id);
      showSnackbar('Download reenfileirado no device-server!', 'success');
      handleRefresh();
    } catch (error: any) {
      showSnackbar(error?.message || 'Erro ao reenfileirar download.', 'error');
    } finally {
      setIsShowLoadingComponent(false);
    }
  };

  return {
    state: {
      items,
      totalCount,
      isLoading,
      isFetching,
      isRetrying,
      pagination,
      devices,
      filters: { deviceId, status, search, startDate, endDate, activeQuickFilter },
      statusCounts: {
        queued: queuedCount,
        processing: processingCount,
        completed: completedCount,
        failed: failedCount,
      },
    },
    actions: {
      setDeviceId,
      setStatus,
      setSearch,
      setStartDate,
      setEndDate,
      setActiveQuickFilter,
      applyFilters,
      handleQuickDateFilter,
      handleClearFilters,
      handleRefresh,
      setPagination,
      handleChangePage,
      handleChangeRowsPerPage,
      handleRetry,
    },
  };
};
